import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles, professionalCategories, serviceCategories, leads } from '@afficheservices/db/schema'
import { sql, isNull, desc, eq } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { formatPrice } from '~/lib/format'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import {
  BarChart3, TrendingUp, MapPin, Users, Building2, ArrowRight,
  CheckCircle, Shield, Wrench, Zap, Paintbrush, Hammer,
  Home, Key, Snowflake, SprayCan, TreePine, Truck, Scissors,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Baromètre des artisans en France 2026 — Statistiques | AfficheServices' },
  { name: 'description', content: 'Baromètre des artisans en France : statistiques sur 10 000+ professionnels du bâtiment. Répartition par métier, ville et département. Données SIRENE officielles.' },
  { name: 'robots', content: 'index, follow' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/barometre' },
]

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush, renovation: Hammer,
  menuiserie: Hammer, toiture: Home, serrurerie: Key, climatisation: Snowflake,
  nettoyage: SprayCan, jardinage: TreePine, demenagement: Truck, 'carrelage-sol': Scissors,
  maconnerie: Building2, isolation: Shield, 'couverture-zinguerie': Home,
  platrerie: Building2, assainissement: Wrench, securite: Shield,
}

export async function loader(_args: LoaderFunctionArgs) {
  // ── Aggregate stats ──────────────────────────────────────
  const [totalRow] = await db.select({ count: sql<number>`count(*)::int` }).from(professionalProfiles)
  const [cityRow] = await db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.city})::int` }).from(professionalProfiles)
  const [deptRow] = await db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.departmentCode})::int` }).from(professionalProfiles)

  // ── By category ──────────────────────────────────────────
  const byCategoryRaw = await db.select({
    name: serviceCategories.nameFr,
    slug: serviceCategories.slug,
    count: sql<number>`count(${professionalCategories.professionalId})::int`,
    leadPriceMin: serviceCategories.leadPriceMin,
    leadPriceMax: serviceCategories.leadPriceMax,
  })
    .from(professionalCategories)
    .innerJoin(serviceCategories, eq(serviceCategories.id, professionalCategories.categoryId))
    .where(isNull(serviceCategories.parentId))
    .groupBy(serviceCategories.nameFr, serviceCategories.slug, serviceCategories.leadPriceMin, serviceCategories.leadPriceMax)
    .orderBy(desc(sql`count(${professionalCategories.professionalId})`))

  // ── By department (top 20) ───────────────────────────────
  const byDept = await db.select({
    dept: professionalProfiles.departmentCode,
    count: sql<number>`count(*)::int`,
  })
    .from(professionalProfiles)
    .groupBy(professionalProfiles.departmentCode)
    .orderBy(desc(sql`count(*)`))
    .limit(20)

  // ── By city (top 20) ─────────────────────────────────────
  const byCity = await db.select({
    city: professionalProfiles.city,
    dept: professionalProfiles.departmentCode,
    count: sql<number>`count(*)::int`,
  })
    .from(professionalProfiles)
    .groupBy(professionalProfiles.city, professionalProfiles.departmentCode)
    .orderBy(desc(sql`count(*)`))
    .limit(20)

  // ── Leads stats ──────────────────────────────────────────
  const [leadTotal] = await db.select({ count: sql<number>`count(*)::int` }).from(leads)

  return {
    totalPros: totalRow?.count ?? 0,
    totalCities: cityRow?.count ?? 0,
    totalDepts: deptRow?.count ?? 0,
    totalLeads: leadTotal?.count ?? 0,
    byCategory: byCategoryRaw,
    byDept,
    byCity,
  }
}

export default function BarometrePage() {
  const { totalPros, totalCities, totalDepts, byCategory, byDept, byCity } = useLoaderData<typeof loader>()
  const maxCatCount = Math.max(...byCategory.map(c => c.count), 1)
  const maxCityCount = Math.max(...byCity.map(c => c.count), 1)

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Baromètre', url: 'https://www.afficheservices.fr/barometre' },
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-blue-200 backdrop-blur-sm">
            <BarChart3 className="h-3.5 w-3.5" /> Données mises à jour quotidiennement
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-4xl lg:text-5xl">
            Baromètre des artisans en France
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-200">
            Statistiques en temps réel sur {totalPros.toLocaleString('fr-FR')}+ professionnels du bâtiment référencés via les données SIRENE officielles.
          </p>

          {/* KPI row */}
          <div className="mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { value: totalPros.toLocaleString('fr-FR') + '+', label: 'Artisans référencés', icon: Users },
              { value: totalCities.toLocaleString('fr-FR'), label: 'Villes couvertes', icon: MapPin },
              { value: String(totalDepts), label: 'Départements', icon: Building2 },
              { value: String(byCategory.length), label: 'Métiers du bâtiment', icon: Wrench },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
                <kpi.icon className="mx-auto h-6 w-6 text-blue-300" />
                <p className="mt-2 text-2xl font-extrabold">{kpi.value}</p>
                <p className="mt-1 text-xs text-blue-300">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 lg:px-6">

        {/* ── By Category ──────────────────────────────── */}
        <section>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Répartition par métier</h2>
              <p className="text-sm text-slate-500">Nombre d'artisans référencés par catégorie</p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {byCategory.map((cat) => {
              const Icon = ICON_MAP[cat.slug] ?? Wrench
              const pct = Math.round((cat.count / maxCatCount) * 100)
              const priceTxt = cat.leadPriceMin && cat.leadPriceMax
                ? `${formatPrice(cat.leadPriceMin, { decimals: 0 })} – ${formatPrice(cat.leadPriceMax, { decimals: 0 })}`
                : null
              return (
                <Link
                  key={cat.slug}
                  to={`/services/${cat.slug}`}
                  className="group flex items-center gap-4 rounded-xl border border-border bg-white p-4 shadow-xs transition-all hover:shadow-md hover:border-primary/30"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform group-hover:scale-110">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900">{cat.name}</span>
                      <span className="shrink-0 text-sm font-bold text-slate-900">
                        {cat.count.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {/* Bar */}
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-blue-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {priceTxt && (
                      <p className="mt-1.5 text-xs text-slate-400">Prix moyen : {priceTxt}</p>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-primary" />
                </Link>
              )
            })}
          </div>
        </section>

        {/* ── By City ──────────────────────────────────── */}
        <section className="mt-14">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
              <MapPin className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Top 20 villes</h2>
              <p className="text-sm text-slate-500">Villes avec le plus de professionnels référencés</p>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {byCity.map((c, i) => {
              const pct = Math.round((c.count / maxCityCount) * 100)
              const citySlug = c.city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <Link
                  key={`${c.city}-${c.dept}`}
                  to={`/travaux/${citySlug}`}
                  className="group flex items-center gap-3 rounded-xl border border-border bg-white p-3.5 shadow-xs transition-all hover:shadow-md hover:border-emerald-200"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900 capitalize">{c.city.toLowerCase()}</span>
                      <span className="shrink-0 text-xs font-bold text-emerald-600">{c.count}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">Département {c.dept}</p>
                  </div>
                </Link>
              )
            })}
          </div>
          <div className="mt-4 text-center">
            <Link to="/toutes-villes" className="text-sm font-semibold text-primary hover:underline">
              Voir les {totalCities.toLocaleString('fr-FR')} villes →
            </Link>
          </div>
        </section>

        {/* ── By Department ────────────────────────────── */}
        <section className="mt-14">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
              <Building2 className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Top 20 départements</h2>
              <p className="text-sm text-slate-500">Concentration de professionnels par département</p>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Département</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Artisans</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Répartition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byDept.map((d, i) => {
                  const pct = Math.round((d.count / (byDept[0]?.count || 1)) * 100)
                  return (
                    <tr key={d.dept} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2.5 text-xs font-bold text-slate-400">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium text-slate-900">{d.dept}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{d.count.toLocaleString('fr-FR')}</td>
                      <td className="hidden px-4 py-2.5 sm:table-cell">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Methodology / Trust ──────────────────────── */}
        <section className="mt-14 rounded-2xl border border-border bg-white p-8 shadow-xs">
          <h2 className="text-xl font-bold text-slate-900">Méthodologie et sources</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[
              { icon: CheckCircle, color: 'text-emerald-500', title: 'Données SIRENE officielles', desc: 'Tous les professionnels sont référencés via le registre SIRENE de l\'INSEE. SIRET vérifié, entreprise active.' },
              { icon: TrendingUp, color: 'text-blue-500', title: 'Mise à jour régulière', desc: 'Les statistiques sont recalculées à chaque consultation. Les nouvelles entreprises apparaissent automatiquement.' },
              { icon: Shield, color: 'text-violet-500', title: 'Filtrage BTP uniquement', desc: 'Seuls les codes NAF du bâtiment (divisions 41-43) et services associés sont inclus dans le baromètre.' },
              { icon: MapPin, color: 'text-amber-500', title: 'Couverture nationale', desc: `${totalDepts} départements, ${totalCities.toLocaleString('fr-FR')} villes, métropole et DOM-TOM inclus.` },
            ].map((item) => (
              <div key={item.title} className="flex gap-3">
                <item.icon className={`mt-0.5 h-5 w-5 shrink-0 ${item.color}`} />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────── */}
        <section className="mt-14 text-center">
          <h2 className="text-2xl font-bold text-slate-900">Trouvez votre artisan</h2>
          <p className="mt-2 text-sm text-slate-500">Comparez les devis gratuits de professionnels vérifiés</p>
          <Link
            to="/demande-devis"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press"
          >
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
